import React from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import Skeleton from 'react-loading-skeleton'
import { useTheme } from 'styled-components'
import { albumCovertArtPlaceholder } from 'images'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { MaterialIcon, Tonality } from '@dsny/dsny-component-library'
import {
  useMobileMediaQuery,
  useTabletAndAboveMediaQuery,
  useXlDownMediaQuery,
  useXlUpMediaQuery
} from 'utils/hooks/useMediaQueries'
import { AIFilled, PlayArrowFilled } from '@dsny/dsny-component-library/icons'
import {
  CoverArt,
  CoverArtContainer,
  CanadianPillWrapper,
  OptionsWrapper,
  PlayIconWrapper
} from '../../ReleaseInformation.styles'
import {
  ReleaseDetailsLayout,
  ReleaseCoverArtSlot,
  ReleaseDetailsCol,
  ReleaseMetaRow,
  ReleaseMetaItem,
  ReleaseMetaSeparator,
  CanadianContentPill,
  ContainsAIPill
} from './ReleaseDetailsNew.styles'

import {
  formatDateWithTimeZoneFromSeconds,
  DATE_FORMAT
} from 'utils/dateHelpers'

const AI_POLICY_URL = 'https://support.plaympe.com/ai-policy' // TODO: Update with actual AI policy URL

import DownloadSectionNew from '../DownloadSection/DownloadSectionNew'
import ActionsSectionNew from '../ActionsSection/ActionsSectionNew'
import ActionsDropdownNew from '../ActionsDropdown/ActionsDropdownNew'
import CoverArtPreview from '../CoverArtPreview/CoverArtPreview'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

const ReleaseDetailsNew = ({
  release,
  setIsModalOpen,
  loading,
  handleDownload,
  isPromoExpanded,
  setRelease
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const isTabletAndAbove = useTabletAndAboveMediaQuery()
  const isXlUp = useXlUpMediaQuery()
  const isXlDown = useXlDownMediaQuery()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()

  const handlePlayRelease = (release) => {
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: PLAY_SOURCES.RELEASE_INFORMATION,
        trigger: PLAY_TRIGGERS.COVER_ART
      })
    )
  }

  const metaItems = [
    {
      key: 'available',
      isVisible: release?.available > 0 || loading,
      content: (
        <>
          {release?.available > 0 &&
            `${t('release-info.available-date')}: ${format(
              new Date(release?.available),
              DATE_FORMAT
            )}`}
          {loading && <Skeleton width={150} />}
        </>
      )
    },
    {
      key: 'impact',
      isVisible: release?.impactdate > 0 || loading,
      content: (
        <>
          {release?.impactdate > 0 &&
            `${t(
              'release-info.impact-date'
            )}: ${formatDateWithTimeZoneFromSeconds(release?.impactdate)}`}
          {loading && <Skeleton width={150} />}
        </>
      )
    },
    {
      key: 'genres',
      isVisible: release?.genres?.length > 0 || loading,
      content: (
        <>
          {release?.genres?.map((genre, index) => (
            <span key={`${genre}-${index}`}>
              {genre} {index < release?.genres.length - 1 && ' / '}
            </span>
          ))}
          {loading && <Skeleton width={200} />}
        </>
      )
    }
  ].filter((item) => item.isVisible)

  return (
    <ReleaseDetailsLayout $responsiveBreakpointsV2={responsiveBreakpointsV2}>
      <ReleaseCoverArtSlot $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        <CoverArtContainer $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          <CoverArt
            src={release?.coverart || albumCovertArtPlaceholder}
            alt="Cover art"
          />
          <CoverArtPreview onExpand={() => setIsModalOpen(true)} />
        </CoverArtContainer>
        {isMobile && (
          <OptionsWrapper>
            <CanadianPillWrapper>
              {release?.markettags?.includes('cancon') && (
                <CanadianContentPill
                  $isMobile={isMobile}
                  variant="tonal"
                  mode="dark"
                  label={t('release-info.canadian')}
                  icon={<MaterialIcon name="CanadaFlag" />}
                  style={{ padding: '4px 8px' }}
                />
              )}
              {release?.is_ai_generated && (
                <ContainsAIPill
                  variant="tonal"
                  mode="dark"
                  label=""
                  onClick={() =>
                    window.open(AI_POLICY_URL, '_blank', 'noopener,noreferrer')
                  }
                  style={{ padding: '4px 8px' }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <AIFilled width={16} height={16} color="currentColor" />
                    <span>{t('release-info.contains-ai')}</span>
                    <MaterialIcon name="OpenInNew" fontSize={14} />
                  </div>
                </ContainsAIPill>
              )}
            </CanadianPillWrapper>
            <PlayIconWrapper>
              <PlayArrowFilled
                width={24}
                color={theme.colors.neutralW20}
                onClick={() => handlePlayRelease(release)}
              />
            </PlayIconWrapper>
          </OptionsWrapper>
        )}
      </ReleaseCoverArtSlot>
      <ReleaseDetailsCol
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        style={{ justifyContent: isMobile ? 'stretch' : 'center' }}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isMobile && release?.markettags?.includes('cancon') && (
            <CanadianContentPill
              $isMobile={isMobile}
              variant="tonal"
              mode="dark"
              label={t('release-info.canadian-content')}
              icon={<MaterialIcon name="CanadaFlag" />}
              style={{ padding: '4px 8px' }}
            />
          )}
          {!isMobile && release?.is_ai_generated && (
            <ContainsAIPill
              variant="tonal"
              mode="dark"
              label=""
              onClick={() =>
                window.open(AI_POLICY_URL, '_blank', 'noopener,noreferrer')
              }
              style={{ padding: '4px 8px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <AIFilled width={16} height={16} color="currentColor" />
                <span>{t('release-info.contains-ai')}</span>
                <MaterialIcon name="OpenInNew" fontSize={14} />
              </div>
            </ContainsAIPill>
          )}
        </div>
        <ReleaseMetaRow>
          <Tonality.UIHeading
            data-cy="release-detail-title"
            headingStyle="headline3"
            tag="h2"
            color={theme.colors.neutralW90}
          >
            {loading || !release?.title ? (
              <Skeleton width={280} />
            ) : (
              `${release?.title} - ${release?.artist}`
            )}
          </Tonality.UIHeading>
        </ReleaseMetaRow>
        <ReleaseMetaRow data-cy="release-detail-meta">
          {metaItems.map((item, index) => {
            const nextItem = metaItems[index + 1]
            const separator = '\u2022'

            return (
              <React.Fragment key={item.key}>
                <ReleaseMetaItem>
                  <Tonality.UIText
                    textStyle="label"
                    color={theme.colors.neutralW80}
                    tag="span"
                    display="inline"
                  >
                    {item.content}
                  </Tonality.UIText>
                </ReleaseMetaItem>
                {nextItem && (
                  <ReleaseMetaSeparator aria-hidden="true">
                    {separator}
                  </ReleaseMetaSeparator>
                )}
              </React.Fragment>
            )
          })}
        </ReleaseMetaRow>

        <div
          style={{
            display: 'flex',
            flexDirection: isTabletAndAbove ? 'row' : 'column',
            alignItems: isTabletAndAbove ? 'center' : 'stretch',
            gap: 16,
            width: '100%'
          }}
        >
          <DownloadSectionNew
            release={release}
            handleDownload={handleDownload}
            isMobile={isMobile}
            isPromoExpanded={isPromoExpanded}
          />

          {isMobile && (
            <ActionsSectionNew release={release} setRelease={setRelease} />
          )}

          {isTabletAndAbove && isXlDown && (
            <ActionsDropdownNew release={release} setRelease={setRelease} />
          )}

          {isXlUp && (
            <ActionsSectionNew release={release} setRelease={setRelease} />
          )}
        </div>
      </ReleaseDetailsCol>
    </ReleaseDetailsLayout>
  )
}

export default ReleaseDetailsNew
